// ConsumerGetOffers.js
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Text,
  View,
  Pressable,
  Platform,
} from 'react-native';
import { useRoute } from '@react-navigation/native';
import {callApi, getTimeMs, getUserId} from '../Helper/Helper';
import mystyles from '../../assets/styles/mystyles';
import {SafeAreaView} from 'react-native-safe-area-context';

const API_BASE_URL = 'https://api.eabu.net';

const KIND = {
  EAT: 'Eat',
  LIVE: 'Live',
  MOVE: 'Move',
};

const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000;

function endpointFor(kind) {
  switch (kind) {
    case KIND.EAT:
      return 'provider-my-eat-services';
    case KIND.LIVE:
      return 'provider-my-live-services';
    case KIND.MOVE:
      return 'provider-my-move-services';
    default:
      return 'provider-my-eat-services';
  }
}

function normalizeItems(data) {
  if (!data) return [];
  if (Array.isArray(data)) return data;
  if (typeof data === 'object') {
    const arrayKey = Object.keys(data).find((k) => Array.isArray(data[k]));
    if (arrayKey) return data[arrayKey];
  }
  return [];
}

const MyServicesList = ({ endpoint, userId, kind }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);

  useEffect(() => {
    const fetchServices = async () => {
      if (!userId) return;
      setLoading(true);
      setError('');
      setData(null);

      try {
        const json = await callApi('GET', `${endpoint}/${encodeURIComponent(userId)}`);
        setData(json);
      } catch (e) {
        setError(e?.message || 'Something went wrong while fetching your services.');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [endpoint, userId]);

  if (loading) {
    return (
      <View style={mystyles.centered}>
        <ActivityIndicator size="large" color="#0A84FF" />
        <Text style={mystyles.loadingText}>Loading services...</Text>
      </View>
    );
  }

  if (error) {
    return (
      <View style={mystyles.errorBox}>
        <Text style={mystyles.errorText}>{error}</Text>
      </View>
    );
  }

  const items = normalizeItems(data);

  const handleRepost = async (item) => {
    const itemId = item?.ID ?? item?.id ?? item?.itemId ?? item?.pk;
    if (!itemId) {
      Alert.alert('Error', 'Could not identify this service ID.');
      return;
    }

    try {
      const itemZipcode = item?.zipcode ?? item?.zip ?? item?.ZIP ?? item?.Zipcode ?? item?.Zip;
      const payload = {
        itemId: itemId,
        serviceCategory: kind,
        zipcode: itemZipcode,
      };

      await callApi('POST', '/provider-repost', { body: payload });
      Alert.alert('Reposted', 'Your service is live again.');
    } catch (e) {
      Alert.alert('Error', e?.message || 'Failed to repost. Please try again.');
    }
  };

  if (items.length === 0) {
    return (
      <View style={{ padding: 20, alignItems: 'center' }}>
        <Text style={{ color: '#666' }}>No services found.</Text>
      </View>
    );
  }

  return (
    <ScrollView contentContainerStyle={{ paddingBottom: 100 }}>
      {items.map((item, idx) => {
        const rowKey = item?.id ?? item?.ID ?? item?.pk ?? String(idx);
        const title = item?.title ?? item?.service ?? 'Service';
        const description = item?.description ?? item?.details ?? '';
        const date = item?.createdAt || item?.CreatedAt;
        const dateMs = date ? getTimeMs(date) : 0;
        const isExpired = Number.isFinite(dateMs) && Date.now() - dateMs > THIRTY_DAYS_MS;
        const interestedByCount = item?.interested_by || 0;

        return (
          <View key={rowKey} style={[mystyles.card, idx % 2 === 1 && mystyles.cardAlt]}>
            <Text style={mystyles.title}>{title}</Text>

            {!!date && (
              <Text style={{ fontSize: 12, color: '#999', marginBottom: 6 }}>
                Posted: {new Date(date).toLocaleDateString()}
              </Text>
            )}

            {!!description && <Text style={mystyles.description}>{description}</Text>}

            {isExpired && (
              <View style={styles.expiredRow}>
                <Text style={styles.expiredText}>Expired</Text>
                <Pressable onPress={() => handleRepost(item)} hitSlop={10}>
                  <Text style={styles.repostLink}>Repost</Text>
                </Pressable>
              </View>
            )}

            <View style={{ flexDirection: 'row', justifyContent: 'flex-start', marginTop: 8, paddingTop: 8, borderTopWidth: 0.5, borderTopColor: '#eee' }}>
              <Text style={{ fontWeight: '600', color: '#444' }}>Interested By:</Text>
              <Text style={{ fontWeight: 'bold', color: '#0A84FF' , paddingLeft:5}}>{interestedByCount}</Text>
            </View>
          </View>
        );
      })}
    </ScrollView>
  );
};

export default function ProviderGetMyServices() {
  const route = useRoute();
  const routeUserId = route?.params?.userId;
  const [userId, setUserId] = useState(routeUserId || '');
  const [activeTab, setActiveTab] = useState(KIND.EAT);

  // Resolve userId if not provided
  useEffect(() => {
    (async () => {
      if (!userId) {
        try {
          const uid = await getUserId();
          setUserId(uid || '');
        } catch {
          // ignore
        }
      }
    })();
  }, [userId]);

  return (
    <SafeAreaView style={mystyles.safe}>
      <View style={styles.headerContainer}>
        <Text style={styles.headerTitle}>My Services</Text>
      </View>

      <View style={styles.tabContainer}>
        <Pressable
          onPress={() => setActiveTab(KIND.EAT)}
          style={[styles.tabButton, activeTab === KIND.EAT && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === KIND.EAT && styles.tabTextActive]}>Food</Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab(KIND.LIVE)}
          style={[styles.tabButton, activeTab === KIND.LIVE && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === KIND.LIVE && styles.tabTextActive]}>Household</Text>
        </Pressable>

        <Pressable
          onPress={() => setActiveTab(KIND.MOVE)}
          style={[styles.tabButton, activeTab === KIND.MOVE && styles.tabButtonActive]}
        >
          <Text style={[styles.tabText, activeTab === KIND.MOVE && styles.tabTextActive]}>Moving</Text>
        </Pressable>
      </View>

      <View style={mystyles.content}>
        {userId ? (
          <>
            {activeTab === KIND.EAT && (
              <MyServicesList endpoint={endpointFor(KIND.EAT)} userId={userId} kind={KIND.EAT} />
            )}
            {activeTab === KIND.LIVE && (
              <MyServicesList endpoint={endpointFor(KIND.LIVE)} userId={userId} kind={KIND.LIVE} />
            )}
            {activeTab === KIND.MOVE && (
              <MyServicesList endpoint={endpointFor(KIND.MOVE)} userId={userId} kind={KIND.MOVE} />
            )}
          </>
        ) : (
          <View style={mystyles.centered}>
            <ActivityIndicator size="large" color="#0A84FF" />
            <Text style={mystyles.loadingText}>Loading user...</Text>
          </View>
        )}
      </View>
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  headerContainer: {
    padding: 15,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
    alignItems: 'center',
  },
  headerTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
  },
  tabContainer: {
    flexDirection: 'row',
    marginHorizontal: 10,
    marginTop: 10,
    marginBottom: 10,
    backgroundColor: '#f1f3f5',
    borderRadius: 30,
    padding: 4,
  },
  tabButton: {
    flex: 1,
    paddingVertical: 10,
    alignItems: 'center',
    borderRadius: 25,
    justifyContent: 'center',
  },
  tabButtonActive: {
    backgroundColor: '#ffffff',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
  },
  tabText: {
    fontWeight: '600',
    color: '#868e96',
    fontSize: 12,
  },
  tabTextActive: {
    color: '#343a40',
    fontWeight: '700',
  },
  expiredRow: {
    marginTop: 10,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  expiredText: {
    color: '#D14343',
    fontWeight: '700',
    fontSize: 12,
  },
  repostLink: {
    color: '#0A84FF',
    fontSize: 12,
    fontWeight: '600',
    textDecorationLine: 'underline',
  },
});
